import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, signal } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { withLogger, withSyncToWebStorage } from '@ngrx-traits/signals';
import {
  EffectiveTheme,
  Theme,
  ThemePreset,
  ThemeState,
} from '../models/theme.model';
import {
  getStoredPreset,
  getStoredTheme,
  migrateStorageFormat,
  THEME_STORAGE_KEY,
  VALID_PRESETS,
  VALID_THEMES,
} from '../utils/theme-storage.utils';

migrateStorageFormat();

const initialState: ThemeState = {
  theme: getStoredTheme(),
  preset: getStoredPreset(),
};

export const ThemeStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => {
    const document = inject(DOCUMENT);
    const systemPrefersDark = signal(
      typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );

    return {
      _document: document,
      _systemPrefersDark: systemPrefersDark,
      _mediaQueryCleanup: null as (() => void) | null,
      _storageCleanup: null as (() => void) | null,
    };
  }),
  withComputed(({ theme, _systemPrefersDark }) => ({
    effectiveTheme: computed<EffectiveTheme>(() => {
      const currentTheme = theme();
      if (currentTheme === 'auto') {
        return _systemPrefersDark() ? 'dark' : 'light';
      }
      return currentTheme;
    }),
    isDarkTheme: computed(() => {
      const currentTheme = theme();
      if (currentTheme === 'auto') {
        return _systemPrefersDark();
      }
      return currentTheme === 'dark';
    }),
  })),
  withLogger({ name: 'ThemeStore', showDiff: true }),
  withSyncToWebStorage({
    key: THEME_STORAGE_KEY,
    type: 'local',
    restoreOnInit: false,
    saveStateChangesAfterMs: 0,
    filterState: ({ theme, preset }: ThemeState) => ({ theme, preset }),
  }),
  withMethods(({ saveToStorage, ...store }) => ({
    setTheme(newTheme: Theme): void {
      patchState(store, { theme: newTheme });
      saveToStorage();
    },
    setPreset(newPreset: ThemePreset): void {
      patchState(store, { preset: newPreset });
      saveToStorage();
      this._applyTheme();
    },
    toggleTheme(): void {
      const current = store.theme();
      let newTheme: Theme;
      if (current === 'auto') {
        const effective = store.effectiveTheme();
        newTheme = effective === 'dark' ? 'light' : 'dark';
      } else {
        newTheme = current === 'light' ? 'dark' : 'light';
      }
      patchState(store, { theme: newTheme });
      saveToStorage();
    },
    _applyTheme(): void {
      const html = store._document.documentElement;
      const effectiveTheme = store.effectiveTheme();
      const preset = store.preset();

      html.classList.remove('light', 'dark');

      if (store.theme() !== 'auto') {
        html.classList.add(effectiveTheme);
      }

      if (preset === 'default') {
        html.removeAttribute('data-preset');
      } else {
        html.setAttribute('data-preset', preset);
      }
    },
    _setupMediaQueryListener(): void {
      if (typeof window === 'undefined') {
        return;
      }

      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handler = (event: MediaQueryListEvent) => {
        store._systemPrefersDark.set(event.matches);
      };

      mediaQuery.addEventListener('change', handler);

      (
        store as { _mediaQueryCleanup: (() => void) | null }
      )._mediaQueryCleanup = () => {
        mediaQuery.removeEventListener('change', handler);
      };
    },
    _setupStorageListener(): void {
      if (typeof window === 'undefined') {
        return;
      }

      const handler = (event: StorageEvent) => {
        if (event.key === THEME_STORAGE_KEY && event.newValue) {
          try {
            const parsed = JSON.parse(event.newValue);
            const updates: Partial<ThemeState> = {};
            if (
              parsed.theme &&
              VALID_THEMES.includes(parsed.theme) &&
              parsed.theme !== store.theme()
            ) {
              updates.theme = parsed.theme;
            }
            if (
              parsed.preset &&
              VALID_PRESETS.includes(parsed.preset) &&
              parsed.preset !== store.preset()
            ) {
              updates.preset = parsed.preset;
            }
            if (Object.keys(updates).length > 0) {
              patchState(store, updates);
            }
          } catch {
            /* ignore invalid JSON from other sources */
          }
        }
      };

      window.addEventListener('storage', handler);

      (store as { _storageCleanup: (() => void) | null })._storageCleanup =
        () => {
          window.removeEventListener('storage', handler);
        };
    },
  })),
  withHooks({
    onInit(store) {
      store._setupMediaQueryListener();
      store._setupStorageListener();
      store._applyTheme();

      effect(() => {
        store.effectiveTheme();
        store.preset();
        store._applyTheme();
      });
    },
    onDestroy(store) {
      store._mediaQueryCleanup?.();
      store._storageCleanup?.();
    },
  })
);
